import { type FormEvent, useEffect, useState } from "react";
import {
  AI_PROVIDER_PRESETS,
  type AiProviderId,
  type AiRuntimeSettings,
  type AiSettingsSaveInput,
  providerPreset,
} from "./settings";

interface AiSettingsPanelProps {
  value: AiRuntimeSettings;
  backendReady: boolean;
  onSave: (input: AiSettingsSaveInput) => Promise<void> | void;
  onTest: (input: AiSettingsSaveInput) => Promise<"ok" | "failed">;
}

function providerBaseUrl(provider: AiProviderId, current: string): string {
  const preset = providerPreset(provider);
  return provider === "openai-compatible" ? current : preset.baseUrl;
}

export function AiSettingsPanel({ value, backendReady, onSave, onTest }: AiSettingsPanelProps) {
  const [settings, setSettings] = useState(value);
  const [primaryApiKey, setPrimaryApiKey] = useState("");
  const [fallbackApiKey, setFallbackApiKey] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSettings(value);
  }, [value]);

  function saveInput(): AiSettingsSaveInput {
    const {
      primaryApiKeyConfigured: _primaryApiKeyConfigured,
      fallbackApiKeyConfigured: _fallbackApiKeyConfigured,
      ...runtimeSettings
    } = settings;

    return {
      settings: runtimeSettings,
      ...(primaryApiKey.trim() ? { primaryApiKey: primaryApiKey.trim() } : {}),
      ...(fallbackApiKey.trim() ? { fallbackApiKey: fallbackApiKey.trim() } : {}),
    };
  }

  function changePrimaryProvider(provider: AiProviderId) {
    setSettings((current) => ({
      ...current,
      primaryProvider: provider,
      primaryBaseUrl: providerBaseUrl(provider, current.primaryBaseUrl),
      primaryModel: "",
    }));
    setPrimaryApiKey("");
  }

  function changeFallbackProvider(provider: AiProviderId) {
    setSettings((current) => ({
      ...current,
      fallbackProvider: provider,
      fallbackBaseUrl: providerBaseUrl(provider, current.fallbackBaseUrl),
      fallbackModel: "",
    }));
    setFallbackApiKey("");
  }

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);

    try {
      await onSave(saveInput());
      setPrimaryApiKey("");
      setFallbackApiKey("");
      setMessage("تنظیمات ذخیره شد.");
    } catch {
      setMessage("ذخیره تنظیمات ناموفق بود.");
    } finally {
      setBusy(false);
    }
  }

  async function handleTest() {
    setBusy(true);
    setMessage(null);

    try {
      const result = await onTest(saveInput());
      setMessage(result === "ok" ? "اتصال هوش مصنوعی موفق بود." : "اتصال برقرار نشد.");
    } catch {
      setMessage("تست اتصال ناموفق بود.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="ai-settings-card" aria-labelledby="ai-settings-title">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Manager settings</p>
          <h2 id="ai-settings-title">تنظیمات هوش مصنوعی</h2>
        </div>
        <label className="switch-row">
          <span>فعال</span>
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(event) =>
              setSettings((current) => ({ ...current, enabled: event.target.checked }))
            }
          />
        </label>
      </div>

      <p className="section-note">
        Provider و مدل را بدون تغییر کد عوض کنید. کلید API فقط برای ذخیره امن به سرور ارسال می‌شود و
        در این صفحه قابل بازیابی نیست.
      </p>

      {!backendReady ? (
        <p className="settings-warning">
          رابط تنظیمات آماده است؛ ذخیره و تست واقعی پس از فعال‌شدن AI Gateway امن در backend در دسترس
          قرار می‌گیرد.
        </p>
      ) : null}

      <form className="ai-settings-form" onSubmit={handleSave}>
        <fieldset>
          <legend>Provider اصلی</legend>

          <label>
            سرویس
            <select
              value={settings.primaryProvider}
              onChange={(event) => changePrimaryProvider(event.target.value as AiProviderId)}
            >
              {AI_PROVIDER_PRESETS.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            مدل
            <input
              type="text"
              value={settings.primaryModel}
              placeholder="نام مدل"
              onChange={(event) =>
                setSettings((current) => ({ ...current, primaryModel: event.target.value }))
              }
            />
          </label>

          <label>
            API endpoint
            <input
              type="url"
              value={settings.primaryBaseUrl}
              readOnly={settings.primaryProvider !== "openai-compatible"}
              placeholder="https://..."
              onChange={(event) =>
                setSettings((current) => ({ ...current, primaryBaseUrl: event.target.value }))
              }
            />
          </label>

          <label>
            API key
            <input
              type="password"
              autoComplete="new-password"
              value={primaryApiKey}
              placeholder={
                settings.primaryApiKeyConfigured
                  ? "کلید قبلاً ثبت شده — برای تعویض وارد کنید"
                  : "API key"
              }
              onChange={(event) => setPrimaryApiKey(event.target.value)}
            />
          </label>
        </fieldset>

        <fieldset>
          <legend>Provider جایگزین</legend>

          <label className="check-row">
            <input
              type="checkbox"
              checked={settings.fallbackEnabled}
              onChange={(event) =>
                setSettings((current) => ({
                  ...current,
                  fallbackEnabled: event.target.checked,
                }))
              }
            />
            <span>در صورت خطای Provider اصلی از جایگزین استفاده شود</span>
          </label>

          <label>
            سرویس جایگزین
            <select
              disabled={!settings.fallbackEnabled}
              value={settings.fallbackProvider}
              onChange={(event) => changeFallbackProvider(event.target.value as AiProviderId)}
            >
              {AI_PROVIDER_PRESETS.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            مدل جایگزین
            <input
              type="text"
              disabled={!settings.fallbackEnabled}
              value={settings.fallbackModel}
              placeholder="نام مدل"
              onChange={(event) =>
                setSettings((current) => ({ ...current, fallbackModel: event.target.value }))
              }
            />
          </label>

          <label>
            API endpoint جایگزین
            <input
              type="url"
              disabled={!settings.fallbackEnabled}
              readOnly={settings.fallbackProvider !== "openai-compatible"}
              value={settings.fallbackBaseUrl}
              placeholder="https://..."
              onChange={(event) =>
                setSettings((current) => ({ ...current, fallbackBaseUrl: event.target.value }))
              }
            />
          </label>

          <label>
            API key جایگزین
            <input
              type="password"
              autoComplete="new-password"
              disabled={!settings.fallbackEnabled}
              value={fallbackApiKey}
              placeholder={
                settings.fallbackApiKeyConfigured
                  ? "کلید قبلاً ثبت شده — برای تعویض وارد کنید"
                  : "API key"
              }
              onChange={(event) => setFallbackApiKey(event.target.value)}
            />
          </label>
        </fieldset>

        <label className="check-row">
          <input
            type="checkbox"
            checked={settings.freeOnly}
            onChange={(event) =>
              setSettings((current) => ({ ...current, freeOnly: event.target.checked }))
            }
          />
          <span>فقط مدل‌ها / مسیرهای رایگان استفاده شوند</span>
        </label>

        <div className="ai-settings-actions">
          <button
            type="button"
            className="secondary-button"
            disabled={busy || !backendReady}
            onClick={handleTest}
          >
            تست اتصال
          </button>
          <button type="submit" className="primary-button" disabled={busy || !backendReady}>
            ذخیره تنظیمات
          </button>
        </div>

        {message ? <p className="form-message">{message}</p> : null}
      </form>
    </section>
  );
}
